import React from "react"
import Progress from "../../../../components/Progress"
import OrgMembershipsTable from "../../../../organisations/components/OrgMembershipsTable"

import { unwrapResult } from "@reduxjs/toolkit"
import { FetchOrgMemberships, FetchUserMemberships } from "../../../../memberships/store"
import { FetchOrganisation } from "../../../../organisations/store/actions"
import { useAppDispatch } from "../../../../store"
import { useAsync } from "../../../../hooks/use-async"
import { FetchContextOptions } from "../../../../context-options/store/actions"
import { useAppContext } from "../../../../app-context/use-app-context"
import { Box, Button, Grid, makeStyles, Tab, Tabs, Theme, Typography } from "@material-ui/core"
import { Plus } from "react-feather"
import { Link } from "react-router-dom"
import { paths } from "../../../../paths"
import { useUrlWithContext } from "../../../../hooks/use-url-with-context"
import { useAuthUserMembership } from "../../../../memberships/hooks/use-auth-user-membership"
import { useIsSmallScreen } from "../../../../hooks/use-is-small-screen"
import OrgMembershipCards from "../../../../organisations/components/OrgMembershipsCards"
import { useTranslation } from "react-i18next"
import HourReportingRoles from "../../../../organisations/components/hour-reporting-roles"
import HourReportingAdmins from "../../../../organisations/components/hour-reporting-admins"
import { useHourReportingWorkflowSettings } from "../../../../hour-reports/hooks/use-hour-reporting-workflow-settings"
import { useGetHourReportAdminsQuery } from "../../../../memberships/api"
import { useMembershipsTabs } from "./use-memberships-tabs"
import NotFound from "../../../../components/NotFound"

type Translations = typeof defaultTranslations

const useLoader = ({ orgId }: { orgId: string }) => {
  const dispatch = useAppDispatch()
  const loader = React.useCallback(async () => {
    dispatch({ type: "load_org_memberships_route" })
    if (orgId) {
      const dispatchResults = await Promise.all([
        dispatch(FetchUserMemberships()),
        dispatch(FetchOrganisation(orgId)),
        dispatch(FetchOrgMemberships(orgId)),
        dispatch(FetchContextOptions()),
      ])
      dispatchResults.forEach(unwrapResult)
    }
  }, [dispatch, orgId])
  return useAsync<void, any>(loader)
}

const OrgMembershipsRoute = () => {
  const { appContext } = useAppContext()
  const orgId = appContext.mainContext?.id as string
  const { error, status } = useLoader({ orgId })
  const errorMessage = error?.message ? error.message : error

  if (status === "error") return <p>{errorMessage} </p>
  if (status === "success") return <OrgMembershipsView orgId={orgId} />

  return <Progress label="" />
}

export default OrgMembershipsRoute

const OrgMembershipsView = ({ orgId }: { orgId: string }) => {
  const classes = useStyles()
  const translations = useTranslations()
  const { appContext } = useAppContext()
  const { mainContext } = appContext
  const {
    canCreateMemberships,
    canUpdateAnyMembershipPermissions,
    membership: authUserMembership,
  } = useAuthUserMembership()
  const { createPathWithContext } = useUrlWithContext()
  const { settings: workflowSettings, isLoading: isLoadingWorkflowSettings } = useHourReportingWorkflowSettings(orgId)
  const { data: hourReportAdminsResponse, isLoading: isLoadingHourReportAdmins } = useGetHourReportAdminsQuery(orgId)
  const canManageHourReportAdmins = hourReportAdminsResponse?.canManageHourReportAdmins ?? false
  const admins = hourReportAdminsResponse?.admins ?? []
  const {
    activeTab,
    activeTabIndex,
    availableTabs,
    handleTabChange,
    hasAvailableTabs,
    isLoading: isLoadingTabs,
  } = useMembershipsTabs({
    canViewMemberships: canCreateMemberships ?? false,
    canViewHourReportingRoles: workflowSettings?.supportsApproval ?? false,
    canViewHourReportingAdmins: canManageHourReportAdmins,
    isLoading: isLoadingWorkflowSettings || isLoadingHourReportAdmins,
  })

  if (!mainContext || mainContext.type === "user") return null
  if (isLoadingTabs) return <Progress label="" />
  if (!hasAvailableTabs) return <NotFound />

  const showAddMemberButton = activeTab === "memberships" && canCreateMemberships

  return (
    <div className={classes.pageContainer}>
      <Grid container alignItems="center" justifyContent="space-between">
        <Grid item>
          <Typography variant="h4" component="header" className={classes.header} data-test="page-header">
            <Box component="span" color="text.secondary">
              {`${mainContext.name} - `}
            </Box>
            {translations.membershipsHeaderText}
          </Typography>
        </Grid>
        <Grid item>
          {showAddMemberButton && (
            <Button
              variant="contained"
              color="primary"
              endIcon={<Plus size={16} />}
              size="small"
              component={Link}
              to={createPathWithContext(paths.orgNewMember(), { withSubContext: false })}
              data-test="add-member-button"
            >
              {translations.addMemberButtonLabel}
            </Button>
          )}
        </Grid>
      </Grid>
      <Tabs
        variant="scrollable"
        value={activeTabIndex}
        onChange={handleTabChange}
        indicatorColor="primary"
        textColor="primary"
        className={classes.tabs}
      >
        {availableTabs.map((tab) => (
          <Tab key={tab.value} className={classes.tab} disableRipple label={translations[tab.labelKey]} />
        ))}
      </Tabs>
      {activeTab === "memberships" && <MembershipsTabPanel orgId={orgId} />}
      {activeTab === "hour-reporting-roles" && workflowSettings && (
        <HourReportingRoles
          orgId={orgId}
          canUpdateCheckersAndApprovers={canUpdateAnyMembershipPermissions || authUserMembership.isOwner}
          supportsChecking={workflowSettings.supportsChecking}
        />
      )}
      {activeTab === "hour-reporting-admins" && workflowSettings && (
        <HourReportingAdmins
          orgId={orgId}
          admins={admins}
          canUpdateAdmins={canManageHourReportAdmins}
          workflowSettings={workflowSettings}
        />
      )}
    </div>
  )
}

const MembershipsTabPanel = ({ orgId }: { orgId: string }) => {
  const isSmallScreen = useIsSmallScreen()

  return isSmallScreen ? <OrgMembershipCards orgId={orgId} /> : <OrgMembershipsTable orgId={orgId} />
}

const defaultTranslations = {
  membershipsHeaderText: "All memberships",
  invitationsHeaderText: "All Invitations",
  addMemberButtonLabel: "Add member",
  membershipsTabLabel: "Memberships",
  hourReportingRolesTabLabel: "Hour reporting roles",
  hourReportingAdminsTabLabel: "Hour reporting admins",
  noHourReportingRolesText: "No hour reporting permissions are available for these roles.",
}

const useTranslations = (): Translations => {
  const { t: tOrg } = useTranslation("org")

  return {
    membershipsHeaderText: tOrg("orgMembershipsPage.membershipsHeaderText", {
      defaultValue: defaultTranslations.membershipsHeaderText,
    }),
    invitationsHeaderText: tOrg("orgMembershipsPage.invitationsHeaderText", {
      defaultValue: defaultTranslations.invitationsHeaderText,
    }),
    addMemberButtonLabel: tOrg("orgMembershipsPage.addMemberButtonLabel", {
      defaultValue: defaultTranslations.addMemberButtonLabel,
    }),
    membershipsTabLabel: tOrg("orgMembershipsPage.membershipsTabLabel", {
      defaultValue: defaultTranslations.membershipsTabLabel,
    }),
    hourReportingRolesTabLabel: tOrg("orgMembershipsPage.hourReportingRolesTabLabel", {
      defaultValue: defaultTranslations.hourReportingRolesTabLabel,
    }),
    hourReportingAdminsTabLabel: tOrg("orgMembershipsPage.hourReportingAdminsTabLabel", {
      defaultValue: defaultTranslations.hourReportingAdminsTabLabel,
    }),
    noHourReportingRolesText: tOrg("orgMembershipsPage.noHourReportingRolesText", {
      defaultValue: defaultTranslations.noHourReportingRolesText,
    }),
  }
}

const useStyles = makeStyles((theme: Theme) => ({
  pageContainer: {
    padding: theme.spacing(0, 2),
    maxWidth: 1440,
    margin: "auto",
  },
  header: {
    margin: theme.spacing(0.75, 0, 2, 0),
  },
  tabs: {
    borderBottom: `1px solid ${theme.palette.divider}`,
    marginBottom: theme.spacing(3),
  },
  tab: {
    minWidth: 120,
  },
}))
